import { providers } from '../ai/registry.js';
import { assetUrl } from '../assets/asset-urls.js';
import { MAX_PARALLEL_GENERATIONS } from '../config/app-config.js';
import { logoPrompt } from '../config/prompts/logo.js';
import { scenePrompt, type Scene } from '../config/prompts/scenes.js';
import { triggeredPrompt, type TriggeredScene } from '../config/prompts/triggered.js';
import { getJson } from '../util/fetch-json.js';
import { brand, customizations, init, prospect as prospectSig, startStatus } from '../state/app-state.js';
import type { Manifest, Slot } from '../types/manifest.js';

const SCENES: Scene[] = ['personal-loan', 'business-credit', 'agricultural'];
const TRIGGERED: TriggeredScene[] = ['tornado', 'multiple_people', 'single_female', 'single_male'];

interface InitJob {
  slot: Slot;
  prompt: string;
  styleType?: 'GENERAL' | 'DESIGN';
  aspectRatio?: '1x1' | '16x9' | '9x16';
  transparent?: boolean;
}

function parseCompanyName(prompt: string): string {
  const quoted = prompt.match(/['"]([^'"]+)['"]/);
  return quoted?.[1] ?? prompt;
}

export interface InitOrchestratorHooks {
  onAssetReady: (slot: Slot, url: string) => void;
  onCompanyName: (name: string) => void;
}

export async function loadManifest(prospect: string): Promise<Manifest> {
  return getJson<Manifest>(`/api/manifest?key=${encodeURIComponent(prospect)}`);
}

export async function initializeProspect(
  prospect: string,
  companyPrompt: string,
  hooks: InitOrchestratorHooks,
): Promise<void> {
  const done = startStatus('Generating signage assets');
  try {
    customizations.set({ slots: {} });
    // 1) Brand brief first — drives logo prompt hints and CTA palette.
    const brief = await providers.text.fetchBrandBrief(prospect, companyPrompt);
    brand.set(brief);
    const companyName = brief.companyName || parseCompanyName(companyPrompt);
    hooks.onCompanyName(companyName);

    // 2) 8 generation jobs, logo uses brief's hints and palette.
    const cur = customizations.get();
    const logoCustom = cur.slots['logo'];
    const logoPromptText = logoCustom?.imagePrompt ?? logoPrompt(companyName, brief.logoPromptHints, brief.palette);
    const defaultJobs = SCENES.map<InitJob>((s) => {
      const slotId = `default.${s}` as const;
      const custom = cur.slots[slotId];
      const promptText = custom?.imagePrompt ?? scenePrompt(s, 'temperate', companyName);
      return { slot: `default.${s}.temperate` as Slot, prompt: promptText };
    });
    const triggeredJobs = TRIGGERED.map<InitJob>((t) => {
      const slotId = `triggered.${t}` as const;
      const custom = cur.slots[slotId];
      const promptText = custom?.imagePrompt ?? triggeredPrompt(t, companyName);
      return { slot: `triggered.${t}` as Slot, prompt: promptText };
    });
    const jobs: InitJob[] = [
      {
        slot: 'logo',
        prompt: logoPromptText,
        styleType: 'DESIGN',
        aspectRatio: '1x1',
        transparent: true,
      },
      ...defaultJobs,
      ...triggeredJobs,
    ];

    let completed = 0;
    const total = jobs.length;
    const queue = [...jobs];
    const inflight = new Set<Promise<void>>();

    async function run(j: InitJob): Promise<void> {
      const generateInput: Parameters<typeof providers.image.generate>[0] = {
        prospect, slot: j.slot, prompt: j.prompt, companyPrompt, companyName,
      };
      if (j.styleType !== undefined) generateInput.styleType = j.styleType;
      if (j.aspectRatio !== undefined) generateInput.aspectRatio = j.aspectRatio;
      if (j.transparent !== undefined) generateInput.transparent = j.transparent;
      const { assetKey } = await providers.image.generate(generateInput);
      hooks.onAssetReady(j.slot, assetUrl(prospect, assetKey));
      completed += 1;
      init.set({ phase: 'generating', key: prospect, completed, total });
    }

    while (queue.length > 0 || inflight.size > 0) {
      while (queue.length > 0 && inflight.size < MAX_PARALLEL_GENERATIONS) {
        const job = queue.shift()!;
        const p = run(job).finally(() => inflight.delete(p));
        inflight.add(p);
      }
      if (inflight.size > 0) await Promise.race(inflight);
    }
    init.set({ phase: 'ready', key: prospect });
  } finally {
    done();
  }
}

export async function loadProspect(prospect: string, hooks: InitOrchestratorHooks): Promise<void> {
  const done = startStatus('Loading prospect');
  try {
    const m = await loadManifest(prospect);
    if (m.brandBrief) brand.set(m.brandBrief);
    if (m.customizations) customizations.set(m.customizations);
    else customizations.set({ slots: {} });
    hooks.onCompanyName(m.companyName || parseCompanyName(m.companyPrompt));
    for (const [slot, asset] of Object.entries(m.slots)) {
      if (asset) hooks.onAssetReady(slot as Slot, assetUrl(prospect, asset.hash));
    }
    prospectSig.set(prospect);
    init.set({ phase: 'ready', key: prospect });
  } finally {
    done();
  }
}
